import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIsMobile } from "../hooks/useIsMobile";

// ─────────────────────────────────────────────────────────────────────────────
// Animated "shape of a case" diagram — both sides file into one record, a gate
// holds until both are in, the panel reads it, one resolution reaches both at
// once. Local + staging only: HomeBeta renders it behind
// isStagingFeature("case-flow"), which production never sets.
//
// Two layouts, same steps: the flow runs left-to-right on desktop (.cfd-h) and
// top-to-bottom on mobile (.cfd-v), so a phone never scrolls sideways. Only one
// is mounted at a time; the geometry that differs between them (flight paths,
// scan offsets, transform origins) is keyed off the layout class in the CSS.
//
// The step animation is driven imperatively (classList toggles on the SVG
// nodes) rather than through state, so a re-render never restarts a run in
// progress. Every className in the markup is a constant, so React's
// reconciliation leaves the classes the timeline adds alone.
//
// CSS lives in the scoped block below, namespaced under #cfd so nothing leaks
// into the rest of the page. Plus Jakarta Sans / DM Mono are already loaded in
// public/index.html.
// ─────────────────────────────────────────────────────────────────────────────

const css = `
#cfd {
  --paper:#F5F5F2;
  --raise:#FFFFFF;
  --ink:#111214;
  --slate:#3A3C40;
  --faint:#7A7C82;
  --blue:#1A5EC4;
  --blue-soft:rgba(26,94,196,0.09);
  --gold:#FEFAEE;
  --gold-line:rgba(200,160,40,0.45);
  --gold-ink:#7A6010;
  --line:rgba(17,18,20,0.13);
  --hair:rgba(17,18,20,0.09);
  --sans:'Plus Jakarta Sans',sans-serif;
  --mono:'DM Mono',monospace;
  border-top:1px solid var(--hair);
  padding:52px 0 64px;
  font-family:var(--sans);
  color:var(--ink);
}
#cfd .cfd-col{max-width:600px;margin:0 auto;padding:0 28px}
#cfd .cfd-wide{max-width:1100px;margin:0 auto;padding:0 28px}
@media(max-width:520px){#cfd{padding:40px 0 48px}#cfd .cfd-col,#cfd .cfd-wide{padding:0 18px}}

#cfd .cfd-kicker{
  font-family:var(--mono);font-size:10px;letter-spacing:0.15em;
  text-transform:uppercase;color:var(--blue);display:block;margin-bottom:14px;
}
#cfd h2{
  font-size:clamp(20px,3vw,24px);font-weight:800;letter-spacing:-0.025em;
  line-height:1.18;margin:0 0 10px;
}
#cfd .cfd-intro{font-size:14px;line-height:1.6;color:var(--slate);margin:0 0 28px}
#cfd .cfd-intro strong{font-weight:600;color:var(--ink)}

#cfd figure{margin:0}
#cfd .cfd-frame{
  background:var(--raise);border:1px solid var(--hair);border-radius:14px;
  padding:16px 16px 8px;overflow-x:auto;
}
#cfd svg{display:block;width:100%;height:auto;color:var(--ink)}
#cfd svg text{font-family:var(--sans);font-size:13px;font-weight:700;fill:currentColor}
#cfd svg text.sub{font-size:11.5px;font-weight:500;fill:var(--slate)}
#cfd svg text.rel{
  font-family:var(--mono);font-size:10px;font-weight:400;letter-spacing:0.08em;
  text-transform:uppercase;fill:var(--faint);
}
#cfd svg text.on-gold{fill:var(--gold-ink)}
/* Wide flow: below its natural width the frame scrolls rather than shrinking the type. */
#cfd .cfd-h{min-width:760px}
/* Tall flow: sized for a phone, so the type is set a little larger to survive the scale-down. */
#cfd .cfd-v{max-width:400px;margin:0 auto}
#cfd .cfd-v text{font-size:14px}
#cfd .cfd-v text.sub{font-size:12px}
#cfd .cfd-v text.rel{font-size:10.5px}

#cfd .cfd-readout{
  display:grid;grid-template-columns:auto 1fr;gap:0 16px;align-items:baseline;
  margin-top:20px;padding-top:16px;border-top:1px solid var(--hair);
}
@media(max-width:520px){#cfd .cfd-readout{grid-template-columns:1fr;gap:6px}}
#cfd .cfd-count{
  font-family:var(--mono);font-size:10px;letter-spacing:0.12em;text-transform:uppercase;
  color:var(--blue);white-space:nowrap;font-variant-numeric:tabular-nums;
}
#cfd .cfd-say{margin:0;font-size:14.5px;line-height:1.6;color:var(--slate);min-height:3em}
#cfd .cfd-say b{color:var(--ink);font-weight:700}
#cfd .cfd-controls{display:flex;flex-wrap:wrap;gap:9px;margin-top:16px}
#cfd .cfd-controls button{
  font-family:var(--sans);font-size:13px;font-weight:600;letter-spacing:-0.01em;
  padding:8px 16px;border-radius:100px;cursor:pointer;
  background:transparent;color:var(--slate);border:1.5px solid var(--line);
  transition:background .15s,color .15s,border-color .15s;
}
#cfd .cfd-controls button:hover{border-color:var(--blue);color:var(--blue)}
#cfd .cfd-controls button[aria-pressed="true"]{background:var(--blue);border-color:var(--blue);color:#fff}
#cfd figcaption{
  margin-top:22px;font-size:13px;line-height:1.7;color:var(--faint);
  padding-left:14px;border-left:2px solid var(--hair);
}
#cfd figcaption b{color:var(--ink);font-weight:700}

/* ── animation states: nodes carry a data-in step index, the run toggles .on ── */
#cfd .node,#cfd .edge,#cfd .flyer,#cfd .page,#cfd .mark,#cfd .resolution{opacity:0;transition:opacity .45s ease}
#cfd .node.on,#cfd .edge.on,#cfd .flyer.on,#cfd .page.on,#cfd .mark.on,#cfd .resolution.on{opacity:1}
/* The panel is on stage from the start, but idle until the gate opens. */
#cfd .panel:not(.lit){opacity:.34}

#cfd .edge path{stroke-dasharray:100;stroke-dashoffset:100;transition:stroke-dashoffset .75s cubic-bezier(.4,.05,.2,1)}
#cfd .edge.on path{stroke-dashoffset:0}

#cfd .flyer{transition:opacity .3s ease,transform .9s cubic-bezier(.45,.05,.2,1)}
#cfd .cfd-h .flyer--you.fly{transform:translate(248px,49px)}
#cfd .cfd-h .flyer--them.fly{transform:translate(248px,-1px)}
#cfd .cfd-h .flyer--wit.fly{transform:translate(248px,-51px)}
#cfd .cfd-v .flyer--you.fly{transform:translate(-20px,182px)}
#cfd .cfd-v .flyer--them.fly{transform:translate(-126px,224px)}
#cfd .cfd-v .flyer--wit.fly{transform:translate(-232px,266px)}

#cfd .record-box{transition:stroke .3s}
#cfd .record-box.pulse{animation:cfd-pulse .7s ease}
@keyframes cfd-pulse{
  0%{stroke-width:2;stroke:currentColor}
  35%{stroke-width:3.4;stroke:#1A5EC4}
  100%{stroke-width:2;stroke:currentColor}
}
#cfd .party.lit .party-box{animation:cfd-pulse .9s ease}

#cfd .scan{opacity:0;transition:opacity .3s,transform .55s cubic-bezier(.4,.05,.2,1)}
#cfd .scan.on{opacity:1}
#cfd .cfd-h .scan.r2{transform:translateY(46px)}
#cfd .cfd-h .scan.r3{transform:translateY(92px)}
#cfd .cfd-v .scan.r2{transform:translateY(50px)}
#cfd .cfd-v .scan.r3{transform:translateY(100px)}

#cfd .beam{transform-box:view-box;transition:transform .5s cubic-bezier(.4,.05,.2,1)}
#cfd .cfd-h .beam{transform-origin:676px 244px}
#cfd .cfd-v .beam{transform-origin:180px 656px}
#cfd .beam.tipA{transform:rotate(-7deg)}
#cfd .beam.tipB{transform:rotate(6deg)}

#cfd .filing{animation:cfd-nudge .6s ease}
@keyframes cfd-nudge{0%,100%{transform:translateY(0)}40%{transform:translateY(-4px)}}
#cfd .face{transform-box:view-box}
#cfd .cfd-h .face--you{transform-origin:48px 78px}
#cfd .cfd-h .face--them{transform-origin:48px 170px}
#cfd .cfd-h .face--wit{transform-origin:48px 262px}
#cfd .cfd-v .face--you{transform-origin:74px 66px}
#cfd .cfd-v .face--them{transform-origin:180px 66px}
#cfd .cfd-v .face--wit{transform-origin:286px 66px}

#cfd .gate-shut,#cfd .gate-open{transition:opacity .3s}
#cfd .gate-open{opacity:0}
#cfd .gate.unlocked .gate-shut{opacity:0}
#cfd .gate.unlocked .gate-open{opacity:1}
#cfd .gate{transform-box:view-box}
#cfd .cfd-h .gate{transform-origin:510px 170px}
#cfd .cfd-v .gate{transform-origin:180px 402px}
#cfd .gate.blocked{animation:cfd-shake .5s ease}
@keyframes cfd-shake{0%,100%{transform:translateX(0)}25%{transform:translateX(-3px)}75%{transform:translateX(3px)}}

#cfd .resolution{transform-box:view-box;transition:opacity .45s ease,transform .5s cubic-bezier(.3,1.4,.5,1)}
#cfd .cfd-h .resolution{transform-origin:897px 170px}
#cfd .cfd-v .resolution{transform-origin:180px 779px}
#cfd .resolution:not(.on){transform:scale(.9)}

@media (prefers-reduced-motion:reduce){
  #cfd .node,#cfd .edge,#cfd .flyer,#cfd .page,#cfd .mark,#cfd .scan,#cfd .resolution,#cfd .beam{transition:none}
  #cfd .edge path{transition:none}
  #cfd .record-box.pulse,#cfd .party.lit .party-box,#cfd .filing,#cfd .gate.blocked{animation:none}
}
`;

// [delay, step number, sentence] — the readout under the figure.
const FULL = [
  [0, 1, "You file — <b>your account of what happened</b>, the outcome you are asking for, and anything that backs it up."],
  [1500, 2, "The other side files their own account from the same blank page. <b>You do not get to frame theirs, and they do not get to frame yours.</b>"],
  [3000, 3, "Witnesses can add an account the same way — optional, up to two, no special standing."],
  [4500, 4, "Both sides are in, so the record opens. <b>This is the gate:</b> a record with one side in it is never read."],
  [6100, 5, "Three AI neutrals read the record end to end — every account, every file."],
  [8500, 6, "A judge weighs all three readings and decides which position prevailed."],
  [10300, 7, "One written resolution — the reasoning, and the outcome — <b>reaches both sides at the same moment</b>."],
];

const ALT = [
  [0, 1, "You file, exactly as before — your account, and your evidence."],
  [1500, 2, "The invitation goes to the other side, and the window runs out with nothing filed."],
  [3200, 3, "<b>The gate holds.</b> The record has one side in it, so the panel never opens it."],
  [4800, 4, "Nothing is judged. The case closes, and you are told it closed without a resolution."],
];

const IDLE = "A case starts empty. Nothing is read, and nothing is decided, until it has both sides in it.";

const ARIA =
  "Both parties and any witnesses file into one shared case record. The record only opens once " +
  "both sides have filed; three AI neutrals then read it in full, a judge weighs them, and one " +
  "written resolution reaches both sides at the same time.";

// ── Wide flow: left to right ────────────────────────────────────────────────
function HorizontalFlow() {
  return (
    <svg className="cfd-h" viewBox="0 0 1000 400" role="img" aria-label={ARIA}>
      <defs>
        <marker id="cfd-ar" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,1 L9,5 L0,9 z" fill="currentColor" />
        </marker>
        <marker id="cfd-ar-b" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,1 L9,5 L0,9 z" fill="#1A5EC4" />
        </marker>
      </defs>

      {/* the two parties and the witnesses */}
      <g className="party party--you">
        <rect className="party-box" x="20" y="44" width="190" height="68" rx="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <g className="face face--you">
          <circle cx="48" cy="66" r="11" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M34,92 a14,14 0 0 1 28,0" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="44.2" cy="64" r="1.4" fill="currentColor" />
          <circle cx="52.2" cy="64" r="1.4" fill="currentColor" />
          <path d="M43,70.5 q5,4 10,0" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </g>
        <text x="88" y="74">You</text>
        <text x="88" y="91" className="sub">account + evidence</text>
      </g>

      <g className="party party--them">
        <rect className="party-box" x="20" y="136" width="190" height="68" rx="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <g className="face face--them">
          <circle cx="48" cy="158" r="11" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M34,184 a14,14 0 0 1 28,0" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="44.2" cy="156" r="1.4" fill="currentColor" />
          <circle cx="52.2" cy="156" r="1.4" fill="currentColor" />
          <path d="M43,162.5 q5,4 10,0" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </g>
        <text x="88" y="166">The other side</text>
        <text x="88" y="183" className="sub">account + evidence</text>
      </g>

      <g className="party party--wit" opacity="0.78">
        <rect className="party-box" x="20" y="228" width="190" height="68" rx="9" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="5 4" />
        <g className="face face--wit">
          <circle cx="40" cy="252" r="8" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M29,272 a11,11 0 0 1 22,0" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <circle cx="37" cy="250.5" r="1.2" fill="currentColor" />
          <circle cx="43" cy="250.5" r="1.2" fill="currentColor" />
          <circle cx="60" cy="256" r="8" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M49,276 a11,11 0 0 1 22,0" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <circle cx="57" cy="254.5" r="1.2" fill="currentColor" />
          <circle cx="63" cy="254.5" r="1.2" fill="currentColor" />
        </g>
        <text x="88" y="258">Witnesses</text>
        <text x="88" y="275" className="sub">optional, up to two</text>
      </g>

      {/* what each party hands in */}
      <g className="flyer flyer--you">
        <rect x="58" y="64" width="20" height="26" rx="3" fill="#FFFFFF" stroke="currentColor" strokeWidth="1.4" />
        <path d="M63,71 h10 M63,76 h10 M63,81 h6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </g>
      <g className="flyer flyer--them">
        <rect x="58" y="156" width="20" height="26" rx="3" fill="#FFFFFF" stroke="currentColor" strokeWidth="1.4" />
        <path d="M63,163 h10 M63,168 h10 M63,173 h6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </g>
      <g className="flyer flyer--wit">
        <rect x="58" y="248" width="20" height="26" rx="3" fill="#FFFFFF" stroke="currentColor" strokeWidth="1.4" />
        <path d="M63,255 h10 M63,260 h10 M63,265 h6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </g>

      {/* inputs into the record */}
      <g className="edge" data-in="1">
        <path d="M210,78 C242,78 244,140 262,146" pathLength="100" stroke="currentColor" strokeWidth="1.5" fill="none" markerEnd="url(#cfd-ar)" />
      </g>
      <g className="edge" data-in="2">
        <path d="M210,170 L262,170" pathLength="100" stroke="currentColor" strokeWidth="1.5" fill="none" markerEnd="url(#cfd-ar)" />
        <text x="236" y="161" textAnchor="middle" className="rel">files</text>
      </g>
      <g className="edge" data-in="3">
        <path d="M210,262 C242,262 244,200 262,194" pathLength="100" stroke="currentColor" strokeWidth="1.5" fill="none" markerEnd="url(#cfd-ar)" opacity="0.72" />
      </g>

      {/* the record */}
      <rect className="record-box" x="270" y="44" width="190" height="252" rx="10" fill="none" stroke="currentColor" strokeWidth="2" />
      <text x="365" y="84" textAnchor="middle">One case record</text>
      <g className="page" data-in="1">
        <rect x="300" y="110" width="124" height="34" rx="4" fill="rgba(26,94,196,0.09)" stroke="currentColor" strokeWidth="1.2" />
        <text x="312" y="132" className="sub">your account</text>
      </g>
      <g className="page" data-in="2">
        <rect x="300" y="152" width="124" height="34" rx="4" fill="rgba(26,94,196,0.09)" stroke="currentColor" strokeWidth="1.2" />
        <text x="312" y="174" className="sub">their account</text>
      </g>
      <g className="page" data-in="3">
        <rect x="300" y="194" width="124" height="34" rx="4" fill="none" stroke="currentColor" strokeWidth="1.2" strokeDasharray="4 3" opacity="0.75" />
        <text x="312" y="216" className="sub">witness account</text>
      </g>
      <text x="365" y="252" textAnchor="middle" className="sub">every file, every word,</text>
      <text x="365" y="269" textAnchor="middle" className="sub">nothing privileged</text>

      {/* the gate */}
      <g className="edge" data-in="1">
        <path d="M460,170 L488,170" pathLength="100" stroke="currentColor" strokeWidth="1.5" fill="none" />
      </g>
      <g className="gate">
        <rect x="492" y="152" width="36" height="36" rx="8" fill="#FFFFFF" stroke="currentColor" strokeWidth="1.5" />
        <g className="gate-shut">
          <rect x="503" y="169" width="14" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M506,169 v-3.5 a4,4 0 0 1 8,0 v3.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
        </g>
        <g className="gate-open">
          <path d="M502,170.5 l5.5,5.5 l10.5,-11" fill="none" stroke="#1A5EC4" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <text x="510" y="142" textAnchor="middle" className="rel">both sides in</text>
      </g>
      <g className="edge" data-in="4">
        <path d="M532,170 L568,170" pathLength="100" stroke="currentColor" strokeWidth="1.5" fill="none" markerEnd="url(#cfd-ar)" />
        <text x="550" y="203" textAnchor="middle" className="rel">read in full</text>
      </g>

      {/* the panel */}
      <g className="node panel" data-in="0">
        <rect x="576" y="44" width="200" height="252" rx="10" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <text x="676" y="82" textAnchor="middle">The AI panel</text>
        <rect className="scan" x="598" y="100" width="156" height="36" rx="6" fill="rgba(26,94,196,0.09)" stroke="#1A5EC4" strokeWidth="1.6" />
        <rect x="598" y="100" width="156" height="36" rx="6" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.55" />
        <text x="676" y="122" textAnchor="middle" className="sub">Neutral 1</text>
        <rect x="598" y="146" width="156" height="36" rx="6" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.55" />
        <text x="676" y="168" textAnchor="middle" className="sub">Neutral 2</text>
        <rect x="598" y="192" width="156" height="36" rx="6" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.55" />
        <text x="676" y="214" textAnchor="middle" className="sub">Neutral 3</text>
        <g className="beam">
          <path d="M648,244 L704,244" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M641,246 a7,7 0 0 0 14,0 z" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M697,246 a7,7 0 0 0 14,0 z" fill="none" stroke="currentColor" strokeWidth="1.4" />
        </g>
        <path d="M676,244 v12" stroke="currentColor" strokeWidth="1.6" />
        <path d="M668,258 h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <text x="676" y="280" textAnchor="middle" className="rel">a judge weighs all three</text>
      </g>

      {/* the resolution */}
      <g className="edge" data-in="6">
        <path d="M776,170 L812,170" pathLength="100" stroke="#1A5EC4" strokeWidth="2" fill="none" markerEnd="url(#cfd-ar-b)" />
        <text x="794" y="158" textAnchor="middle" className="rel">decides</text>
      </g>
      <g className="resolution" data-in="6">
        <rect x="820" y="110" width="162" height="120" rx="10" fill="#FEFAEE" stroke="rgba(200,160,40,0.45)" strokeWidth="2" />
        <text x="901" y="150" textAnchor="middle" className="on-gold">Written resolution</text>
        <text x="901" y="172" textAnchor="middle" className="sub">the reasoning, and which</text>
        <text x="901" y="189" textAnchor="middle" className="sub">position prevailed</text>
        <path d="M873,206 l6,6 l12,-12" fill="none" stroke="#7A6010" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M896,206 h32" stroke="rgba(200,160,40,0.45)" strokeWidth="2" strokeLinecap="round" />
      </g>

      {/* delivered to both, on one stroke */}
      <g className="edge" data-in="7">
        <path d="M901,230 L901,352 L9,352 L9,78 L15,78" pathLength="100" stroke="#1A5EC4" strokeWidth="2" fill="none" markerEnd="url(#cfd-ar-b)" />
        <path d="M9,352 L9,170 L15,170" pathLength="100" stroke="#1A5EC4" strokeWidth="2" fill="none" markerEnd="url(#cfd-ar-b)" />
        <text x="508" y="341" textAnchor="middle" className="rel">both sides, the same moment</text>
      </g>

      {/* the closed-case marker (one-sided run only) */}
      <g className="mark">
        <rect x="576" y="312" width="200" height="40" rx="8" fill="#FFFFFF" stroke="currentColor" strokeWidth="1.5" strokeDasharray="5 4" />
        <text x="676" y="337" textAnchor="middle" className="sub">nothing is judged · case closes</text>
      </g>
    </svg>
  );
}

// ── Tall flow: top to bottom, for phones ────────────────────────────────────
function VerticalFlow() {
  return (
    <svg className="cfd-v" viewBox="0 0 360 900" role="img" aria-label={ARIA}>
      <defs>
        <marker id="cfd-arv" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,1 L9,5 L0,9 z" fill="currentColor" />
        </marker>
        <marker id="cfd-arv-b" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0,1 L9,5 L0,9 z" fill="#1A5EC4" />
        </marker>
      </defs>

      {/* the two parties and the witnesses, side by side across the top */}
      <g className="party party--you">
        <rect className="party-box" x="24" y="20" width="100" height="92" rx="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <g className="face face--you">
          <circle cx="74" cy="48" r="11" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M60,74 a14,14 0 0 1 28,0" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="70.2" cy="46" r="1.4" fill="currentColor" />
          <circle cx="78.2" cy="46" r="1.4" fill="currentColor" />
          <path d="M69,52.5 q5,4 10,0" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </g>
        <text x="74" y="92" textAnchor="middle">You</text>
        <text x="74" y="106" textAnchor="middle" className="sub">+ evidence</text>
      </g>

      <g className="party party--them">
        <rect className="party-box" x="130" y="20" width="100" height="92" rx="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <g className="face face--them">
          <circle cx="180" cy="48" r="11" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M166,74 a14,14 0 0 1 28,0" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="176.2" cy="46" r="1.4" fill="currentColor" />
          <circle cx="184.2" cy="46" r="1.4" fill="currentColor" />
          <path d="M175,52.5 q5,4 10,0" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </g>
        <text x="180" y="92" textAnchor="middle">Other side</text>
        <text x="180" y="106" textAnchor="middle" className="sub">+ evidence</text>
      </g>

      <g className="party party--wit" opacity="0.78">
        <rect className="party-box" x="236" y="20" width="100" height="92" rx="9" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="5 4" />
        <g className="face face--wit">
          <circle cx="276" cy="46" r="8" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M265,66 a11,11 0 0 1 22,0" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <circle cx="273" cy="44.5" r="1.2" fill="currentColor" />
          <circle cx="279" cy="44.5" r="1.2" fill="currentColor" />
          <circle cx="294" cy="50" r="8" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M283,70 a11,11 0 0 1 22,0" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <circle cx="291" cy="48.5" r="1.2" fill="currentColor" />
          <circle cx="297" cy="48.5" r="1.2" fill="currentColor" />
        </g>
        <text x="286" y="92" textAnchor="middle">Witnesses</text>
        <text x="286" y="106" textAnchor="middle" className="sub">up to two</text>
      </g>

      {/* what each party hands in */}
      <g className="flyer flyer--you">
        <rect x="84" y="38" width="18" height="24" rx="3" fill="#FFFFFF" stroke="currentColor" strokeWidth="1.4" />
        <path d="M88,45 h10 M88,50 h10 M88,55 h6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </g>
      <g className="flyer flyer--them">
        <rect x="190" y="38" width="18" height="24" rx="3" fill="#FFFFFF" stroke="currentColor" strokeWidth="1.4" />
        <path d="M194,45 h10 M194,50 h10 M194,55 h6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </g>
      <g className="flyer flyer--wit">
        <rect x="296" y="38" width="18" height="24" rx="3" fill="#FFFFFF" stroke="currentColor" strokeWidth="1.4" />
        <path d="M300,45 h10 M300,50 h10 M300,55 h6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </g>

      {/* inputs converging into the record */}
      <g className="edge" data-in="1">
        <path d="M74,112 C74,142 120,150 170,158" pathLength="100" stroke="currentColor" strokeWidth="1.5" fill="none" markerEnd="url(#cfd-arv)" />
      </g>
      <g className="edge" data-in="2">
        <path d="M180,112 L180,158" pathLength="100" stroke="currentColor" strokeWidth="1.5" fill="none" markerEnd="url(#cfd-arv)" />
        <text x="186" y="128" className="rel">files</text>
      </g>
      <g className="edge" data-in="3">
        <path d="M286,112 C286,142 240,150 190,158" pathLength="100" stroke="currentColor" strokeWidth="1.5" fill="none" markerEnd="url(#cfd-arv)" opacity="0.72" />
      </g>

      {/* the record */}
      <rect className="record-box" x="30" y="164" width="300" height="200" rx="10" fill="none" stroke="currentColor" strokeWidth="2" />
      <text x="180" y="196" textAnchor="middle">One case record</text>
      <g className="page" data-in="1">
        <rect x="56" y="216" width="248" height="36" rx="4" fill="rgba(26,94,196,0.09)" stroke="currentColor" strokeWidth="1.2" />
        <text x="68" y="239" className="sub">your account</text>
      </g>
      <g className="page" data-in="2">
        <rect x="56" y="258" width="248" height="36" rx="4" fill="rgba(26,94,196,0.09)" stroke="currentColor" strokeWidth="1.2" />
        <text x="68" y="281" className="sub">their account</text>
      </g>
      <g className="page" data-in="3">
        <rect x="56" y="300" width="248" height="36" rx="4" fill="none" stroke="currentColor" strokeWidth="1.2" strokeDasharray="4 3" opacity="0.75" />
        <text x="68" y="323" className="sub">witness account</text>
      </g>
      <text x="180" y="353" textAnchor="middle" className="sub">every file, every word, nothing privileged</text>

      {/* the gate */}
      <g className="edge" data-in="1">
        <path d="M180,364 L180,382" pathLength="100" stroke="currentColor" strokeWidth="1.5" fill="none" />
      </g>
      <g className="gate">
        <rect x="162" y="384" width="36" height="36" rx="8" fill="#FFFFFF" stroke="currentColor" strokeWidth="1.5" />
        <g className="gate-shut">
          <rect x="173" y="401" width="14" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M176,401 v-3.5 a4,4 0 0 1 8,0 v3.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
        </g>
        <g className="gate-open">
          <path d="M172,402.5 l5.5,5.5 l10.5,-11" fill="none" stroke="#1A5EC4" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </g>
        <text x="206" y="406" className="rel">both sides in</text>
      </g>
      <g className="edge" data-in="4">
        <path d="M180,420 L180,444" pathLength="100" stroke="currentColor" strokeWidth="1.5" fill="none" markerEnd="url(#cfd-arv)" />
        <text x="206" y="438" className="rel">read in full</text>
      </g>

      {/* the panel */}
      <g className="node panel" data-in="0">
        <rect x="30" y="448" width="300" height="246" rx="10" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <text x="180" y="480" textAnchor="middle">The AI panel</text>
        <rect className="scan" x="56" y="500" width="248" height="40" rx="6" fill="rgba(26,94,196,0.09)" stroke="#1A5EC4" strokeWidth="1.6" />
        <rect x="56" y="500" width="248" height="40" rx="6" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.55" />
        <text x="180" y="525" textAnchor="middle" className="sub">Neutral 1</text>
        <rect x="56" y="550" width="248" height="40" rx="6" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.55" />
        <text x="180" y="575" textAnchor="middle" className="sub">Neutral 2</text>
        <rect x="56" y="600" width="248" height="40" rx="6" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.55" />
        <text x="180" y="625" textAnchor="middle" className="sub">Neutral 3</text>
        <g className="beam">
          <path d="M150,656 L210,656" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M143,658 a7,7 0 0 0 14,0 z" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M203,658 a7,7 0 0 0 14,0 z" fill="none" stroke="currentColor" strokeWidth="1.4" />
        </g>
        <path d="M180,656 v12" stroke="currentColor" strokeWidth="1.6" />
        <path d="M172,670 h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <text x="180" y="688" textAnchor="middle" className="rel">a judge weighs all three</text>
      </g>

      {/* the resolution */}
      <g className="edge" data-in="6">
        <path d="M180,694 L180,716" pathLength="100" stroke="#1A5EC4" strokeWidth="2" fill="none" markerEnd="url(#cfd-arv-b)" />
        <text x="206" y="710" className="rel">decides</text>
      </g>
      <g className="resolution" data-in="6">
        <rect x="30" y="720" width="300" height="118" rx="10" fill="#FEFAEE" stroke="rgba(200,160,40,0.45)" strokeWidth="2" />
        <text x="180" y="754" textAnchor="middle" className="on-gold">Written resolution</text>
        <text x="180" y="776" textAnchor="middle" className="sub">the reasoning, and which</text>
        <text x="180" y="793" textAnchor="middle" className="sub">position prevailed</text>
        <path d="M138,812 l6,6 l12,-12" fill="none" stroke="#7A6010" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M161,812 h40" stroke="rgba(200,160,40,0.45)" strokeWidth="2" strokeLinecap="round" />
      </g>

      {/* delivered to both, on one stroke — back up the left margin */}
      <g className="edge" data-in="7">
        <path d="M180,838 L180,884 L8,884 L8,8 L74,8 L74,16" pathLength="100" stroke="#1A5EC4" strokeWidth="2" fill="none" markerEnd="url(#cfd-arv-b)" />
        <path d="M8,8 L180,8 L180,16" pathLength="100" stroke="#1A5EC4" strokeWidth="2" fill="none" markerEnd="url(#cfd-arv-b)" />
        <text x="100" y="871" textAnchor="middle" className="rel">both sides, the same moment</text>
      </g>

      {/* the closed-case marker (one-sided run only) */}
      <g className="mark">
        <rect x="30" y="744" width="300" height="40" rx="8" fill="#FFFFFF" stroke="currentColor" strokeWidth="1.5" strokeDasharray="5 4" />
        <text x="180" y="769" textAnchor="middle" className="sub">nothing is judged · case closes</text>
      </g>
    </svg>
  );
}

export default function CaseFlowDiagram() {
  const isMobile = useIsMobile();
  const rootRef = useRef(null);
  const timers = useRef([]);
  const started = useRef(false);
  const layout = useRef(isMobile);
  const [altMode, setAltMode] = useState(false);
  const [say, setSay] = useState({ n: 0, total: 7, text: IDLE });

  const run = useCallback((alt) => {
    const root = rootRef.current;
    if (!root) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const q = (sel) => root.querySelector(sel);

    timers.current.forEach(clearTimeout);
    timers.current = [];
    const at = (ms, fn) => timers.current.push(setTimeout(fn, reduced ? 0 : ms));

    const show = (step) => {
      root.querySelectorAll("[data-in]").forEach((el) => {
        el.classList.toggle("on", step >= +el.dataset.in);
      });
    };
    const flash = (el, cls, ms) => {
      if (reduced || !el) return;
      el.classList.add(cls);
      at(ms, () => el.classList.remove(cls));
    };

    const gate = q(".gate");
    const scan = q(".scan");
    const beam = q(".beam");
    const panel = q(".panel");
    const closed = q(".mark");
    const record = q(".record-box");

    // reset
    show(0);
    root.querySelectorAll(".flyer").forEach((d) => d.classList.remove("on", "fly"));
    root.querySelectorAll(".party").forEach((p) => p.classList.remove("lit"));
    gate.classList.remove("unlocked", "blocked");
    scan.classList.remove("on", "r2", "r3");
    beam.classList.remove("tipA", "tipB");
    panel.classList.remove("lit");
    closed.classList.remove("on");

    const steps = alt ? ALT : FULL;
    steps.forEach(([t, n, text]) => at(t, () => setSay({ n, total: steps.length, text })));

    // A party's paper flies from their hands into its slot in the record.
    const file = (who, delay) => {
      const doc = q(`.flyer--${who}`);
      at(delay, () => {
        doc.classList.add("on");
        flash(q(`.face--${who}`), "filing", 600);
      });
      at(delay + 260, () => doc.classList.add("fly"));
      at(delay + 1050, () => {
        doc.classList.remove("on");
        flash(record, "pulse", 700);
      });
    };

    file("you", 100);
    at(200, () => show(1));

    if (alt) {
      at(3300, () => flash(gate, "blocked", 500));
      at(4900, () => closed.classList.add("on"));
      return;
    }

    file("them", 1600);
    at(1700, () => show(2));
    file("wit", 3100);
    at(3200, () => show(3));

    at(4700, () => gate.classList.add("unlocked"));
    at(5000, () => { show(4); panel.classList.add("lit"); });

    at(6200, () => scan.classList.add("on"));
    at(7000, () => scan.classList.add("r2"));
    at(7800, () => scan.classList.add("r3"));
    at(8600, () => { scan.classList.remove("on"); beam.classList.add("tipA"); });
    at(9200, () => { beam.classList.remove("tipA"); beam.classList.add("tipB"); });
    at(9800, () => beam.classList.remove("tipB"));
    at(10000, () => show(6));
    at(10500, () => show(7));
    at(11500, () => {
      root.querySelectorAll(".party--you, .party--them").forEach((p) => p.classList.add("lit"));
      at(12500, () => root.querySelectorAll(".party").forEach((p) => p.classList.remove("lit")));
    });
  }, []);

  // Start when the section scrolls into view, not on mount — it sits at the
  // bottom of the page and would otherwise finish before anyone sees it.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started.current) {
          started.current = true;
          run(altMode);
        }
      },
      { threshold: 0.25 }
    );
    io.observe(root);
    return () => {
      io.disconnect();
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, [run, altMode]);

  // Crossing the breakpoint swaps in the other layout, which drops the classes
  // the run had applied — play it again so it doesn't strand mid-sequence.
  useEffect(() => {
    if (layout.current === isMobile) return;
    layout.current = isMobile;
    if (started.current) run(altMode);
  }, [isMobile, altMode, run]);

  const onReplay = () => run(altMode);
  const onToggleAlt = () => {
    const next = !altMode;
    setAltMode(next);
    run(next);
  };

  return (
    <section id="cfd" ref={rootRef}>
      <style>{css}</style>

      <div className="cfd-col">
        <span className="cfd-kicker">The shape of it</span>
        <h2>One record. Read once. Both sides told at the same moment.</h2>
        <p className="cfd-intro">
          The whole process, a step at a time. <strong>The one structural thing worth watching is
          the gate in the middle:</strong> the panel never opens a record that has only one side in it.
        </p>
      </div>

      <div className="cfd-wide">
        <figure>
          <div className="cfd-frame">
            {isMobile ? <VerticalFlow /> : <HorizontalFlow />}
          </div>

          <div className="cfd-readout" aria-live="polite">
            <span className="cfd-count">Step {say.n} / {say.total}</span>
            <p className="cfd-say" dangerouslySetInnerHTML={{ __html: say.text }} />
          </div>

          <div className="cfd-controls">
            <button type="button" onClick={onReplay}>Replay</button>
            <button type="button" aria-pressed={altMode} onClick={onToggleAlt}>
              {altMode ? "Play the full run" : "If only one side files"}
            </button>
          </div>

          <figcaption>
            <b>One record, read once.</b> Neither side gets to frame the other's submission, and
            neither receives the resolution before the other. If only one side files, nothing is
            judged at all.
          </figcaption>
        </figure>
      </div>
    </section>
  );
}
